import { useEffect, useRef, useState } from "react";

// Simple time-based fake progress that approaches a cap and completes on demand.
// Usage:
// const { percent, start, complete, reset, running } = useFakeProgress({ totalMs: 45000, cap: 92 });
// start(); // begin animating to ~cap%
// complete(); // jump to 100%
export default function useFakeProgress(options = {}) {
  const totalMs = options.totalMs ?? 45000; // observed average ~45s
  const cap = Math.max(1, Math.min(99, options.cap ?? 92));

  const [percent, setPercent] = useState(0);
  const [running, setRunning] = useState(false);
  const startedAtRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!running) return;
    startedAtRef.current = Date.now();

    const tick = () => {
      const started = startedAtRef.current ?? Date.now();
      const elapsed = Date.now() - started;
      const t = Math.max(0, Math.min(1, elapsed / totalMs));
      // ease out curve: quick rise then slow near cap
      const eased = 1 - Math.exp(-3 * t);
      const target = cap * eased;
      setPercent((p) => Math.min(cap, Math.max(p, target)));
    };

    timerRef.current = window.setInterval(tick, 200);

    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };
  }, [running, totalMs, cap]);

  const start = () => {
    setPercent(0);
    setRunning(true);
  };

  const complete = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    setRunning(false);
    setPercent(100);
  };

  const reset = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    setRunning(false);
    setPercent(0);
  };

  return { percent, running, start, complete, reset };
}